import React from "react";
import { Box, Typography } from "@mui/material";

const SCROLL_SX = {
  boxShadow: "inset 0 -12px 10px -10px rgba(0, 0, 0, 0.08)",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    background: "rgba(0, 0, 0, 0.12)",
    borderRadius: 3,
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "rgba(0, 0, 0, 0.2)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "rgba(0, 0, 0, 0.12) transparent",
} as const;

export interface CollapsibleSetupGuidePanelProps {
  /** Unique id for the scroll container (collapse resets scroll to top). */
  contentId: string;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  children: React.ReactNode;
}

/**
 * Peek + expand/collapse panel for long setup instructions in Add Integration.
 * Shared by Hudu, IT Glue, and Halo so behavior and affordances stay consistent.
 * Grep: Setup guide collapsible, Click to expand instructions
 */
export const CollapsibleSetupGuidePanel: React.FC<CollapsibleSetupGuidePanelProps> = ({
  contentId,
  expanded,
  onExpandedChange,
  children,
}) => (
  <Box
    sx={{
      mb: 2,
      bgcolor: "#f9f9f9",
      borderRadius: 1,
      position: "relative",
      cursor: expanded ? "default" : "pointer",
    }}
    onClick={() => {
      if (!expanded) onExpandedChange(true);
    }}
  >
    <Box
      id={contentId}
      sx={{
        p: 2,
        maxHeight: expanded ? 350 : 120,
        overflow: expanded ? "auto" : "hidden",
        ...(expanded ? SCROLL_SX : {}),
      }}
    >
      {children}
    </Box>

    {!expanded ? (
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 60,
          background: "linear-gradient(transparent, #f9f9f9)",
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
          borderRadius: 1,
          pb: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 13,
            fontWeight: 600,
            color: "#705d4a",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Click to expand instructions
        </Typography>
      </Box>
    ) : null}

    {expanded ? (
      <Box sx={{ display: "flex", justifyContent: "center", pb: 1 }}>
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            document.getElementById(contentId)?.scrollTo(0, 0);
            onExpandedChange(false);
          }}
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontSize: 13,
            fontWeight: 600,
            color: "#705d4a",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Click to collapse
        </Typography>
      </Box>
    ) : null}
  </Box>
);
